import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import { useAuthed } from '../hooks/useAuthed';
import { gymApi } from '../api/gymApi';
import type { ProgramState, ProgramSummary } from '../../../shared/types';

/**
 * Program selection, then weak-point selection, as two full pages (no modals).
 * The weak point is chosen once here and resolves every "Weak Point Exercise"
 * row in the plan.
 */
export default function ProgramsPage() {
  const authed = useAuthed();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<ProgramSummary[] | null>(null);
  const [state, setState] = useState<ProgramState | null>(null);
  const [picked, setPicked] = useState<ProgramSummary | null>(null);
  const [weakPoint, setWeakPoint] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Inline confirm for the destructive "restart from day 1" action.
  const [confirmRestart, setConfirmRestart] = useState(false);

  const load = useCallback(() => {
    Promise.all([gymApi.listPrograms(authed), gymApi.getProgramState(authed)])
      .then(([p, s]) => { setPrograms(p); setState(s); })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load programs'));
  }, [authed]);

  useEffect(load, [load]);

  // restart=true wipes progress back to day 1; the default preserves it, so
  // continuing the current program is safe.
  async function confirm(restart = false) {
    if (!picked) return;
    setBusy(true);
    setError('');
    try {
      await gymApi.startProgram(picked.id, weakPoint, authed, restart);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the program');
      setBusy(false);
    }
  }

  // ── Step 2: weak point ──────────────────────────────────────────────────
  if (picked) {
    const isCurrent = !!state?.enrolled && state.programId === picked.id;
    const isSwitch = !!state?.enrolled && state.programId !== picked.id;
    return (
      <>
        <SiteHeader back={{ label: 'Programs', onClick: () => { setPicked(null); setConfirmRestart(false); } }} />
        <main className="page page--narrow">
          <h1>Pick your weak point</h1>
          <p className="muted" style={{ marginBottom: 16 }}>
            {picked.title} has dedicated weak-point slots. Your choice fills those
            rows for the whole program — you can change it later.
          </p>

          <ul className="plain-list">
            {picked.weakPoints.map(m => (
              <li key={m}>
                <button
                  type="button"
                  className={`choice-row${weakPoint === m ? ' choice-row--selected' : ''}`}
                  onClick={() => setWeakPoint(m)}
                >
                  <span className="choice-radio" aria-hidden="true" />
                  <span>{m}</span>
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                className={`choice-row${weakPoint === null ? ' choice-row--selected' : ''}`}
                onClick={() => setWeakPoint(null)}
              >
                <span className="choice-radio" aria-hidden="true" />
                <span>Skip for now</span>
              </button>
            </li>
          </ul>

          {isCurrent && (
            <p className="muted" style={{ marginTop: 16 }}>
              You're already on this program — continuing keeps your day and
              completed workouts.
            </p>
          )}
          {isSwitch && (
            <p className="warn" style={{ marginTop: 16 }}>
              Switching programs restarts you at day 1. Your completed workouts and
              exercise history are kept.
            </p>
          )}
          {error && <p className="error">{error}</p>}

          <button
            type="button"
            className="btn-primary btn-block btn-lg"
            style={{ marginTop: 20 }}
            onClick={() => confirm(false)}
            disabled={busy}
          >
            {busy
              ? 'Saving…'
              : isCurrent ? `Continue ${picked.title}`
              : isSwitch ? `Switch to ${picked.title}`
              : `Start ${picked.title}`}
          </button>

          {/* Restarting the current program is destructive, so it is separate
              from Continue and asks first. */}
          {isCurrent && (
            confirmRestart ? (
              <div className="restart-confirm">
                <span className="warn">Restart from day 1? Your completed workouts stay in history.</span>
                <div className="restart-confirm-actions">
                  <button type="button" className="btn-quiet btn-sm" onClick={() => setConfirmRestart(false)} disabled={busy}>
                    Cancel
                  </button>
                  <button type="button" className="btn-danger btn-sm" onClick={() => confirm(true)} disabled={busy}>
                    Yes, restart
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn-quiet btn-block btn-sm"
                style={{ marginTop: 10 }}
                onClick={() => setConfirmRestart(true)}
                disabled={busy}
              >
                Restart from day 1
              </button>
            )
          )}
        </main>
      </>
    );
  }

  // ── Step 1: program ─────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader back={{ label: 'Home', to: '/' }} />
      <main className="page page--narrow">
        <h1>Programs</h1>
        {error && <p className="error">{error}</p>}
        {!programs && !error && <p className="muted">Loading…</p>}

        {state?.enrolled && (
          <p className="muted" style={{ marginBottom: 16 }}>
            Currently on <strong>{state.title}</strong> — day {state.cursor} of {state.totalDays}
            {state.weakPoint ? ` · weak point: ${state.weakPoint}` : ''}
          </p>
        )}

        <ul className="plain-list">
          {programs?.map(p => {
            const isCurrent = state?.enrolled && state.programId === p.id;
            return (
              <li key={p.id}>
                <button type="button" className="program-card" onClick={() => setPicked(p)}>
                  <div className="program-card-head">
                    <span className="program-card-title">{p.title}</span>
                    {isCurrent && <span className="badge">current</span>}
                  </div>
                  <span className="muted">
                    {p.weeks} weeks · {p.workoutDays} workouts · {p.totalDays} days
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </main>
    </>
  );
}
